import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { HSlider } from "../components/HSlider";
import { Twitch, ExternalLink, UserPlus, Eye, Mail, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { twitchParents } from "../lib/twitch";
import { ClipsSection } from "../components/ClipsSection";

const FEATURED_IMG = "https://images.pexels.com/photos/15367435/pexels-photo-15367435.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function LiveDot({ live, className = "" }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${live ? "bg-manticore animate-blink" : "bg-valkyra"} ${className}`}
      data-testid={live ? "live-dot-online" : "live-dot-offline"}
    />
  );
}

function SchedulePanel() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/schedule").then(({ data }) => setItems(data.schedule)).catch(() => setItems([]));
  }, []);

  const KIND_META = {
    live: { color: "#34C759", label: t("str.kind_live") },
    off: { color: "#FF3B30", label: t("str.kind_off") },
    special: { color: "#FF5C00", label: t("str.kind_special") },
  };

  const dayLabel = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(`${dateStr}T12:00:00`);
    const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - today) / 86400000);
    if (diff === 0) return t("str.sched_today");
    if (diff === 1) return t("str.sched_tomorrow");
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { weekday: "short", day: "2-digit", month: "short" }).toUpperCase();
  };

  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 flex flex-col" data-testid="schedule-panel">
      <div className="flex items-center gap-2.5 mb-5">
        <CalendarDays className="w-4 h-4 text-wardogs shrink-0" />
        <p className="hud-label">{t("str.schedule_title")}</p>
      </div>
      {items === null ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-[#050505] border border-[#1A1A1A] animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="font-mono text-[11px] text-[#4A4D51] leading-relaxed" data-testid="schedule-empty">{t("str.schedule_empty")}</p>
      ) : (
        <div className="flex flex-col gap-4" data-testid="schedule-list">
          {Object.values(items.reduce((acc, it) => {
            (acc[it.date] = acc[it.date] || { date: it.date, sessions: [] }).sessions.push(it);
            return acc;
          }, {})).map((g) => {
            const first = g.sessions[0];
            const meta = KIND_META[first.kind] || KIND_META.live;
            const accent = first.color || meta.color;
            return (
              <div key={g.date} className="border-l-2 pl-4" style={{ borderLeftColor: accent }} data-testid={`schedule-day-${g.date}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>{dayLabel(g.date)}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#4A4D51]">
                    {g.sessions.length > 1 ? `${g.sessions.length}× ${meta.label}` : meta.label}
                  </span>
                </div>
                <div className="flex flex-col gap-2.5">
                  {g.sessions.map((it) => (
                    <div key={it.id} className="flex items-center gap-3" data-testid={`schedule-item-${it.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.color || KIND_META[it.kind]?.color || "#34C759" }} />
                      {it.image && (
                        <img src={it.image} alt="" className="w-10 h-10 object-cover chamfer-sm border border-[#1A1A1A] shrink-0" data-testid={`schedule-img-${it.id}`} />
                      )}
                      <div className="min-w-0 flex items-baseline gap-2.5 flex-wrap">
                        {it.time && <span className="font-display text-2xl leading-none text-white">{it.time}</span>}
                        {it.title && <span className="font-mono text-[11px] text-steel leading-snug">{it.title}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FeaturedStreamer({ channel, live }) {
  const { t } = useLang();
  const parentParams = twitchParents();
  return (
    <section className="pt-32 pb-20 relative overflow-hidden" data-testid="featured-streamer-section">
      <div className="absolute inset-0">
        <img src={FEATURED_IMG} alt="" className="w-full h-full object-cover opacity-15 grayscale" />
        <div className="absolute inset-0 bg-[#050505]/70" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("str.over")}</p>
          <div className="flex items-center gap-4 mb-4">
            <a href={`https://www.twitch.tv/${channel}`} target="_blank" rel="noopener noreferrer" data-testid="featured-name-link" className="hover:opacity-90 transition-opacity duration-300">
              <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9]">
                IlQuartier<span className="text-wardogs">Mastro</span>
              </h1>
            </a>
            {live?.is_live && <LiveDot live={true} className="mt-4" />}
          </div>
          <p className="text-steel text-base md:text-lg max-w-xl mb-10">
            {t("str.text")}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid lg:grid-cols-[320px_1fr_320px] gap-6 items-stretch">
            <SchedulePanel />
            <div className="chamfer border border-[#1A1A1A] overflow-hidden bg-[#0C0C0C] relative">
              <div className="absolute top-0 left-0 right-0 h-px bg-wardogs/60 z-10" />
              <div className="aspect-video">
                <iframe
                  src={`https://player.twitch.tv/?channel=${channel}&${parentParams}&autoplay=false`}
                  title={`Twitch — ${channel}`}
                  className="w-full h-full"
                  allowFullScreen
                  data-testid="twitch-player-embed"
                />
              </div>
            </div>
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 flex flex-col justify-between gap-6">
              <div>
                <p className="hud-label mb-4">{t("str.status")}</p>
                <div className="flex items-center gap-2 mb-2" data-testid="featured-live-status">
                  <LiveDot live={live?.is_live} />
                  <span className={`font-mono text-xs uppercase tracking-[0.2em] ${live?.is_live ? "text-manticore" : "text-valkyra"}`}>
                    {live?.is_live ? t("str.live") : t("str.offline")}
                  </span>
                </div>
                {live?.is_live ? (
                  <div className="mb-6">
                    <p className="text-sm text-white mb-1" data-testid="featured-live-title">{live.title}</p>
                    <p className="font-mono text-[10px] text-steel flex items-center gap-1.5">
                      <Eye className="w-3 h-3" /> {t("live.viewers", { n: live.viewers })}
                    </p>
                  </div>
                ) : (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel mb-6">{t("str.follow")}</p>
                )}
                <p className="text-sm text-steel leading-relaxed">
                  {t("str.sub_note")}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href={`https://www.twitch.tv/${channel}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="featured-twitch-link"
                  className="chamfer-sm bg-[#9146FF] hover:bg-[#7d3aed] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 text-center transition-colors duration-300"
                >
                  {t("str.open")}
                </a>
                <a
                  href={`https://www.twitch.tv/subs/${channel}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="featured-subscribe-link"
                  className="chamfer-sm border border-wardogs text-wardogs hover:bg-wardogs hover:text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 text-center transition-colors duration-300"
                >
                  {t("str.subscribe")}
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PartnerCard({ partner, live }) {
  const { t } = useLang();
  const parentParams = twitchParents();
  const isLive = live?.is_live;
  return (
    <div className="group relative p-8 overflow-hidden h-full" data-testid={`partner-card-${partner.twitch_login}`}>
      <div className="flex items-start justify-between mb-6">
        <Twitch className="w-6 h-6 text-[#9146FF]" />
        <div className="flex items-center gap-2">
          <LiveDot live={isLive} />
          <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${isLive ? "text-manticore" : "text-valkyra"}`} data-testid={`partner-status-${partner.twitch_login}`}>
            {isLive ? t("str.live") : t("str.offline")}
          </span>
        </div>
      </div>
      <a
        href={`https://www.twitch.tv/${partner.twitch_login}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`partner-name-link-${partner.twitch_login}`}
        className="block group/name"
      >
        <p className="font-display text-3xl uppercase mb-1 group-hover/name:text-wardogs group-hover:text-wardogs transition-colors duration-300 underline-offset-4 group-hover/name:underline decoration-wardogs/50">
          {partner.display_name}
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel group-hover/name:text-white transition-colors duration-300">twitch.tv/{partner.twitch_login}</p>
      </a>
      {partner.note && <p className="mt-3 text-sm text-steel">{partner.note}</p>}
      {isLive && (
        <div className="mt-5">
          <p className="text-xs text-white mb-2 truncate" data-testid={`partner-live-title-${partner.twitch_login}`}>{live.title}</p>
          <div className="relative chamfer-sm border border-[#1A1A1A] overflow-hidden">
            <div className="aspect-video pointer-events-none">
              <iframe
                src={`https://player.twitch.tv/?channel=${partner.twitch_login}&${parentParams}&autoplay=true&muted=true`}
                title={`Twitch — ${partner.twitch_login}`}
                className="w-full h-full"
                data-testid={`partner-player-${partner.twitch_login}`}
              />
            </div>
            <a
              href={`https://www.twitch.tv/${partner.twitch_login}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`partner-watch-${partner.twitch_login}`}
              className="absolute inset-0 flex items-center justify-center bg-[#050505]/0 hover:bg-[#050505]/50 transition-colors duration-300"
            >
              <span className="opacity-0 group-hover:opacity-100 font-mono text-[10px] uppercase tracking-[0.3em] text-wardogs transition-opacity duration-300">
                {t("str.watch")} →
              </span>
            </a>
          </div>
        </div>
      )}
      {!isLive && (
        <a
          href={`https://www.twitch.tv/${partner.twitch_login}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-2 text-steel hover:text-wardogs transition-colors duration-300"
        >
          <ExternalLink className="w-4 h-4" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">twitch.tv</span>
        </a>
      )}
    </div>
  );
}

function PartnerGrid({ partners, streams }) {
  const { t } = useLang();
  // Pannelli sempre a multipli di 3: minimo 3, crescono coi partner fino a 9 slot totali
  const target = Math.min(9, Math.max(3, Math.ceil(Math.max(partners.length, 1) / 3) * 3));
  const slots = [...partners];
  while (slots.length < target) slots.push(null);

  const slideCls = "snap-start shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] border border-[#1A1A1A] bg-[#050505]";

  return (
    <section className="py-24 border-t border-[#1A1A1A]" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("str.partners_over")}</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-4">{t("str.partners_title")}</h2>
          <p className="text-steel max-w-lg mb-14">{t("str.partners_text")}</p>
        </Reveal>
        <HSlider testid="partners-slider">
          {slots.map((p, i) => (
            <div key={p ? p.twitch_login : `empty-${i}`} className={slideCls}>
              {p ? (
                <PartnerCard partner={p} live={streams?.[p.twitch_login]} />
              ) : (
                <div className="p-8 h-full min-h-[220px] flex flex-col justify-between border border-dashed border-[#2A2A2A]" data-testid={`partner-slot-empty-${i}`}>
                  <UserPlus className="w-6 h-6 text-[#2A2A2A]" />
                  <div>
                    <p className="font-display text-2xl uppercase text-[#4A4D51]">{t("str.slot")}</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D51]">{t("str.slot_sub")}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </HSlider>

        <Reveal delay={0.15}>
          <div className="chamfer border border-wardogs/40 bg-[#0C0C0C] mt-12 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="partner-join-box">
            <div className="flex-1">
              <p className="hud-label mb-2 text-wardogs">// UNISCITI AL PROGETTO</p>
              <p className="font-display text-3xl uppercase leading-tight mb-2">Vuoi diventare Streamer Partner?</p>
              <p className="text-sm text-steel leading-relaxed max-w-xl">
                Scrivici una email presentando il tuo canale: ti risponderemo il prima possibile. Il pulsante apre in automatico il tuo servizio email; in caso di problemi, copia l'indirizzo qui sotto e scrivici come preferisci.
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full md:w-auto shrink-0">
              <a
                href="mailto:wigfounder@gmail.com?subject=Richiesta%20Streamer%20Partner%20WARDOGS.HQ&body=Ciao!%20Vorrei%20entrare%20nel%20progetto%20Streamer%20Partner.%0A%0ACanale%20Twitch%3A%0ANome%3A%0AOrari%20tipici%20di%20live%3A%0A"
                className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300 flex items-center justify-center gap-3"
                data-testid="partner-join-button"
              >
                <Mail className="w-4 h-4" /> Invia richiesta
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText("wigfounder@gmail.com").then(() => toast.success("Email copiata: wigfounder@gmail.com")).catch(() => {}); }}
                className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-white font-mono text-[11px] px-5 py-2.5 transition-colors duration-300 flex items-center justify-center gap-2"
                data-testid="partner-copy-email"
              >
                <Copy className="w-3.5 h-3.5" /> wigfounder@gmail.com
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Streamers() {
  const [data, setData] = useState({ featured: "ilquartiermastro", partners: [] });
  const [streams, setStreams] = useState({});

  useEffect(() => {
    api.get("/streamers").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const logins = [data.featured, ...data.partners.map((p) => p.twitch_login)].join(",");
    const check = () => api.get(`/live?logins=${encodeURIComponent(logins)}`).then(({ data }) => setStreams(data.streams)).catch(() => {});
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [data]);

  return (
    <main>
      <FeaturedStreamer channel={data.featured} live={streams[data.featured]} />
      <ClipsSection />
      <PartnerGrid partners={data.partners} streams={streams} />
    </main>
  );
}
